import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppShell } from "@/components/AppShell";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireSubscription } from "@/components/RequireSubscription";
import { RequireAdmin } from "@/components/RequireAdmin";
import { LoadingScreen } from "@/components/LoadingScreen";
import Index from "./pages/Index";

const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const Screenings = lazy(() => import("./pages/Screenings"));
const ScreeningRunner = lazy(() => import("./pages/ScreeningRunner"));
const ScreeningResult = lazy(() => import("./pages/ScreeningResult"));
const ScreeningHistory = lazy(() => import("./pages/ScreeningHistory"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Chat = lazy(() => import("./pages/Chat"));
const Journal = lazy(() => import("./pages/Journal"));
const Resources = lazy(() => import("./pages/Resources"));
const Settings = lazy(() => import("./pages/Settings"));
const NativeAuthBridge = lazy(() => import("./pages/NativeAuthBridge"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const Admin = lazy(() => import("./pages/Admin"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Legal = lazy(() => import("./pages/Legal"));
const Security = lazy(() => import("./pages/Security"));
const Support = lazy(() => import("./pages/Support"));
const Mood = lazy(() => import("./pages/Mood"));
const Triage = lazy(() => import("./pages/Triage"));
const SupportDirectory = lazy(() => import("./pages/SupportDirectory"));
const Learn = lazy(() => import("./pages/Learn"));
const Grounding = lazy(() => import("./pages/Grounding"));
const ShareResult = lazy(() => import("./pages/ShareResult"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AppShell>
              <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/complete-profile" element={<RequireAuth><CompleteProfile /></RequireAuth>} />
                <Route path="/screenings" element={<Screenings />} />
                <Route path="/screening/:id" element={<ScreeningRunner />} />
                <Route path="/screening-result/:id" element={<ScreeningResult />} />
                <Route path="/history" element={<RequireAuth><ScreeningHistory /></RequireAuth>} />
                <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="/chat" element={<RequireAuth><RequireSubscription><Chat /></RequireSubscription></RequireAuth>} />
                <Route path="/journal" element={<RequireAuth><RequireSubscription><Journal /></RequireSubscription></RequireAuth>} />
                <Route path="/resources" element={<RequireAuth><RequireSubscription><Resources /></RequireSubscription></RequireAuth>} />
                <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
                <Route path="/subscribe" element={<RequireAuth><Subscribe /></RequireAuth>} />
                <Route path="/checkout/return" element={<RequireAuth><CheckoutReturn /></RequireAuth>} />
                <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
                <Route path="/admin/subscriptions" element={<RequireAdmin><Admin /></RequireAdmin>} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/security" element={<Security />} />
                <Route path="/support" element={<Support />} />
                <Route path="/mood" element={<Mood />} />
                <Route path="/triage" element={<Triage />} />
                <Route path="/support-directory" element={<SupportDirectory />} />
                <Route path="/learn" element={<Learn />} />
                <Route path="/grounding" element={<Grounding />} />
                <Route path="/r/:token" element={<ShareResult />} />
                <Route path="/native-auth-bridge" element={<NativeAuthBridge />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </AppShell>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
